import { useState, useEffect } from 'react';
import { supabase } from '../lib/db.ts';
import { logger } from '../services/logger.ts';

export interface PromoConfig {
  id: string;
  promo_name: string;
  is_active: boolean;
  starting_balance: number;
  start_date: string | null;
  end_date: string | null;
  show_leaderboard?: boolean;
  show_profit_leaderboard?: boolean;
  show_affiliate_leaderboard?: boolean;
  show_user_affiliate_count?: boolean;
  show_admin_profit_leaderboard?: boolean;
  show_admin_affiliate_leaderboard?: boolean;
  competition_terms_content?: string | null;
  competition_terms_version?: number;
  competition_terms_updated_at?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Hook to fetch active promo config from database
 * Returns the active promo config or null if none is active
 */
export function usePromoConfig() {
  const [promoConfig, setPromoConfig] = useState<PromoConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPromoConfig = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch active promo config from database
        const { data, error: fetchError } = await supabase
          .from('promo_config')
          .select('*')
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (fetchError) {
          // If table doesn't exist or error, return null (no promo active)
          logger.warn('Error fetching promo config:', fetchError);
          setPromoConfig(null);
          return;
        }

        if (data) {
          setPromoConfig(data as PromoConfig);
        } else {
          setPromoConfig(null);
        }
      } catch (err) {
        logger.error('Error in usePromoConfig:', err);
        setError(err instanceof Error ? err.message : 'Failed to load promo config');
        setPromoConfig(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPromoConfig();

    // Refresh every 60 seconds to catch updates
    const interval = setInterval(fetchPromoConfig, 60000);
    return () => clearInterval(interval);
  }, []);

  return { promoConfig, loading, error };
}

/**
 * Helper function to check if deposits should be disabled
 */
export function areDepositsDisabled(promoConfig: PromoConfig | null): boolean {
  return promoConfig?.is_active === true;
}

/**
 * Helper function to get promo message for disabled deposits
 */
export function getDepositDisabledMessage(promoConfig: PromoConfig | null): string {
  if (!promoConfig?.is_active) {
    return 'Deposits are currently disabled.';
  }
  return `Deposits are disabled during the ${promoConfig.promo_name}. Everyone starts with R${promoConfig.starting_balance}!`;
}

